/**
 * A shell that never moves (UX spec v3 §C.9; research 11 §2 items 2–7, 12, snippet B).
 *
 * On iOS the keyboard shrinks only the VISUAL viewport: `100dvh`, `position: fixed`
 * and `innerHeight` stay the same, so Safari pans the page to reveal the field and
 * the header goes with it. The one tool iOS has today is the Visual Viewport API:
 * `#app` is `position: fixed` at `top: var(--vvtop)` with `height: var(--vvh)`, fed
 * from here, so the header stays where it was, the composer sits on the keyboard and
 * the stream (the one scroller) shrinks. `html[data-kbd]` hides the tab bar and the
 * bottom inset while the keyboard is up (item 7).
 *
 * DCTA-89 (Umakhan's installed iPhone, 32.jpg: a band between the composer and the keyboard):
 * installed on iOS, `innerHeight` shrinks WITH the visual viewport when the keyboard opens (and
 * can keep the shrink after it closes — dev.to/cederhook, "the iOS standalone-PWA keyboard bug");
 * Android with `interactive-widget=resizes-content` does the same. Measured against `innerHeight`
 * the keyboard was never "up", so the dock kept the 34 px home-indicator inset under the composer.
 * The keyboard is now measured against the TALLEST view seen at this width, and only while a
 * field has focus — no field, no keyboard, whatever a stuck viewport says.
 *
 * Known limit (WebKit bug 218465): `visualViewport` scroll events may arrive only
 * when the pan ends, so the composer can land a beat after the keyboard. A device
 * check, not a promise.
 *
 * Zoom (item 12, Umakhan's ruling of 29 Sep: "no zoom under any condition"):
 *  - focus zoom is gone by CSS (every editable ≥ 16 px, app.css 27);
 *  - double-tap zoom by `touch-action: manipulation` (app.css 27);
 *  - installed (standalone): the viewport meta gains `maximum-scale=1, user-scalable=no`;
 *  - in the Safari tab (which ignores that meta since iOS 10): Safari's `gesturestart`
 *    and any two-finger `touchmove` are cancelled — best effort, UNVERIFIED on iOS 26/27.
 * System Zoom (three-finger double-tap) still works outside the page.
 *
 * Selection (Umakhan, recording rec-d, 29 Sep): a long press must never select text
 * or open Copy / Look Up / Translate — app.css 28 turns selection off app-wide and
 * back on only in fields; `contextmenu` is cancelled here outside fields.
 */
export interface VisualViewportLike {
  readonly height: number;
  readonly offsetTop: number;
  readonly scale: number;
  addEventListener(type: "resize" | "scroll", fn: () => void): void;
  removeEventListener(type: "resize" | "scroll", fn: () => void): void;
}

/** The keyboard (not Safari's collapsing toolbar) takes more than 120 px (research 11 item 7). */
const KEYBOARD_MIN = 120;
/** "At the end of the stream": within this many px of its bottom. */
const ANCHOR = 4;

export function keyboardUp(visibleHeight: number, layoutHeight: number): boolean {
  return visibleHeight < layoutHeight - KEYBOARD_MIN;
}

/** The tallest view seen at one width — the height with no keyboard, whatever innerHeight says now. */
export interface Tallest {
  width: number;
  height: number;
}

export function tallest(memory: Tallest, width: number, ...heights: number[]): number {
  const w = Math.round(width);
  if (w !== memory.width) {
    memory.width = w;
    memory.height = 0;
  }
  memory.height = Math.max(memory.height, ...heights);
  return memory.height;
}

/** A field the OS keyboard serves: a text-like input, a textarea, an editable region; never read-only. */
export function isEditable(el: Element | null): boolean {
  if (el === null) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    const NOT_TEXT = ["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"];
    return !NOT_TEXT.includes(el.type) && !el.readOnly && !el.disabled;
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

export interface FitEnv {
  readonly root: HTMLElement;
  readonly vv: VisualViewportLike | null;
  readonly innerHeight: () => number;
  readonly innerWidth: () => number;
  /** A field has focus — the only time the OS keyboard can be up. */
  readonly editing: () => boolean;
  /** The tallest view seen at this width (one per shell). */
  readonly memory: Tallest;
  /** The one scroller on screen now (`#app .body`), if any. */
  readonly scroller: () => HTMLElement | null;
}

export function fit(env: FitEnv): void {
  const vv = env.vv;
  // Never re-lay the shell out while the customer is pinch-zoomed (system Zoom, or a
  // pinch the tab could not stop): the numbers describe the zoomed view, not the app.
  if (vv === null || vv.scale > 1.01) return;
  const s = env.scroller();
  const anchored = s !== null && s.scrollHeight - s.scrollTop - s.clientHeight <= ANCHOR;
  const style = env.root.style;
  style.setProperty("--vvh", `${String(Math.round(vv.height))}px`);
  style.setProperty("--vvtop", `${String(Math.round(vv.offsetTop))}px`);
  const full = tallest(env.memory, env.innerWidth(), env.innerHeight(), vv.height);
  env.root.setAttribute("data-kbd", env.editing() && keyboardUp(vv.height, full) ? "true" : "false");
  // The stream shrank under a newest line that was in view: it stays in view (§C.9).
  if (anchored && s !== null) s.scrollTop = s.scrollHeight;
}

const LOCK = ["maximum-scale=1", "user-scalable=no"];

/** The viewport meta's content: the lock is added only when the app runs installed. */
export function viewportContent(content: string, standalone: boolean): string {
  if (!standalone) return content;
  const parts = content.split(",").map((p) => p.trim()).filter((p) => p !== "");
  const keys = new Set(parts.map((p) => p.split("=")[0]?.trim()));
  for (const lock of LOCK) if (!keys.has(lock.split("=")[0])) parts.push(lock);
  return parts.join(", ");
}

type MatchMedia = (query: string) => { readonly matches: boolean };

export function isStandalone(matchMedia: MatchMedia, nav: { readonly standalone?: boolean }): boolean {
  return matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

export interface ShellEnv {
  readonly doc: Document;
  readonly vv: VisualViewportLike | null;
  readonly innerHeight: () => number;
  readonly innerWidth: () => number;
  readonly matchMedia: MatchMedia;
  readonly navigator: { readonly standalone?: boolean };
  readonly scroller: () => HTMLElement | null;
  readonly raf: (fn: () => void) => void;
}

/** Wire the shell once at boot. Returns a stop for tests. */
export function installShell(env: ShellEnv): () => void {
  const root = env.doc.documentElement;
  const standalone = isStandalone(env.matchMedia, env.navigator);
  root.setAttribute("data-standalone", standalone ? "true" : "false");
  const meta = env.doc.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (meta !== null) meta.content = viewportContent(meta.content, standalone);

  const memory: Tallest = { width: 0, height: 0 };
  const editing = (): boolean => isEditable(env.doc.activeElement);
  const refit = (): void =>
    fit({ root, vv: env.vv, innerHeight: env.innerHeight, innerWidth: env.innerWidth, editing, memory, scroller: env.scroller });
  env.vv?.addEventListener("resize", refit);
  env.vv?.addEventListener("scroll", refit);
  env.doc.addEventListener("focusin", () => env.raf(refit));
  // The field let go: no keyboard from here, even if iOS leaves the view shrunk (DCTA-89).
  env.doc.addEventListener("focusout", () => env.raf(refit));
  // iOS paints `:active` only when a touch listener exists (Apple's retired guide, item 5).
  env.doc.addEventListener("touchstart", () => undefined, { passive: true });
  // No long-press menu (Copy / Look Up / Translate) outside a field — Umakhan's ruling after
  // recording rec-d (29 Sep); copying is an explicit Copy button. CSS stops the selection
  // itself (app.css 28); this stops the menu and the desktop right-click menu.
  env.doc.addEventListener("contextmenu", (e) => {
    const t = e.target;
    if (t instanceof Element && t.closest("input, textarea, [contenteditable]:not([contenteditable='false'])") !== null) return;
    e.preventDefault();
  });
  // The Safari tab ignores user-scalable: cancel its pinch gesture and any two-finger move.
  env.doc.addEventListener("gesturestart", (e) => e.preventDefault(), { passive: false });
  env.doc.addEventListener(
    "touchmove",
    (e) => {
      if ((e as TouchEvent).touches.length > 1) e.preventDefault();
    },
    { passive: false },
  );
  refit();
  return () => {
    env.vv?.removeEventListener("resize", refit);
    env.vv?.removeEventListener("scroll", refit);
  };
}

/**
 * Research 11 item 11: iOS has no install prompt, so the door says how — only in a browser on
 * iOS (an iPhone, or an iPad that reports a Mac with touch), never once installed.
 */
export function installHintWanted(nav: { readonly userAgent: string; readonly platform: string; readonly maxTouchPoints: number }, standalone: boolean): boolean {
  if (standalone) return false;
  return /\b(iPhone|iPad|iPod)\b/.test(nav.userAgent) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
}
